/**
 * Implements Trades Saga
 * Main idea is to fetch trades each time new block mined for ETH
 */
import { eventChannel } from '@redux-saga/core';
import { IDexTrade } from '@kattana-io/common';
import {
  takeLatest,
  put,
  call,
  select,
  take,
  fork,
  cancel,
} from 'redux-saga/effects';
import { SWITCH } from '../actions/current';
import {
  actionLoadTrades,
  actionAppendTrade,
  actionUpdateDexHistory,
  actionBeforeAppendTrade,
  ACTION_BEFORE_ADD_TRADE,
  ACTION_LOAD_TRADES,
  actionClearDexTrades,
  actionUpdateTradesMeta,
} from '../actions/dex_trades';
import eventEmitter from '../core/eventEmitter';
import { calcDexTrades, fillHashesFromData } from '../helpers/calcDexTrades';
import { getLiveTradesEventName } from '../helpers/events-builder';
import { KattanaAPI, subscription } from '../store/createStore';
import { IStore } from '../reducers';

const subs: any[] = [];

type lastTradeHandlerType = ({ trade }: { trade: IDexTrade }) => void;

/**
 * If currently we have selected ETH exchange than do request
 * Set loader if we are not loading now
 * @param payload
 */
function* onSwitchPair(): any {
  const loading = yield select((state) => state.dex_trades.loading);

  if (!loading) {
    yield put(
      actionUpdateDexHistory({
        loading: true,
        trades: [],
      })
    );
  }
  yield put(actionLoadTrades());
}

function* tradesEventsChannel(liveTradesEventName: string): any {
  let lastTradeHandler: lastTradeHandlerType = () => undefined;

  const chan = eventChannel<any>((emitter) => {
    lastTradeHandler = ({ trade }) => {
      emitter(trade);
    };
    eventEmitter.on(liveTradesEventName, lastTradeHandler);
    // eslint-disable-next-line no-empty-function
    return () => {};
  });

  try {
    while (true) {
      const trade = yield take(chan);
      yield put(actionBeforeAppendTrade(trade));
    }
  } finally {
    eventEmitter.removeListener(liveTradesEventName, lastTradeHandler);
    console.log(
      'event channel for live trades terminated ',
      liveTradesEventName
    );
  }
}

function* onLoadCurrentTrades(): any {
  const ticker = yield select((state: IStore) => state.current);

  if (ticker.chain) {
    const data: any = yield call(
      KattanaAPI.trades,
      ticker.chain,
      ticker.address,
      ticker.flipped
    );
    // Data could be null
    if (data) {
      const { byBlock, byWallet } = yield select(
        (state: IStore) => state.dex_trades.meta
      );

      const hashedData = fillHashesFromData(data, byBlock, byWallet);
      yield put(actionUpdateTradesMeta(hashedData));

      yield put(
        actionUpdateDexHistory({
          loading: false,
          trades: calcDexTrades(
            data,
            ticker.base_decimals,
            ticker.quote_decimals,
            ticker.flipped,
            hashedData.byBlock,
            hashedData.byWallet
          ),
        })
      );
    } else {
      yield put(actionClearDexTrades());
    }
    if (subs.length > 0) {
      yield cancel(subs);
      subs.splice(0, subs.length);
    }

    const liveTradesEventName = getLiveTradesEventName(
      ticker.chain,
      ticker.base,
      ticker.quote
    );

    const ch = yield fork(tradesEventsChannel, liveTradesEventName);
    subs.push(ch);

    subscription.subscribeToLiveTrades(liveTradesEventName, ticker);
  }
}

function* onTradeAddIntent(action: any): any {
  const { flipped, base_decimals, quote_decimals } = yield select(
    (state: any) => state.current
  );
  const { byBlock, byWallet } = yield select(
    (state: IStore) => state.dex_trades.meta
  );

  const { payload } = action;

  const hashedData = fillHashesFromData([payload], byBlock, byWallet);
  yield put(actionUpdateTradesMeta(hashedData));

  const [res] = calcDexTrades(
    [payload],
    base_decimals,
    quote_decimals,
    flipped,
    hashedData.byBlock,
    hashedData.byWallet
  );

  yield put(actionAppendTrade(res));
}

export default function* tradesSaga() {
  yield takeLatest(SWITCH, onSwitchPair);
  yield takeLatest(ACTION_LOAD_TRADES, onLoadCurrentTrades);
  yield takeLatest(ACTION_BEFORE_ADD_TRADE, onTradeAddIntent);
}
